import {
  DefaultError,
  InfiniteData,
  useInfiniteQuery,
  useQueryClient,
} from '@tanstack/react-query';
import Pagination from 'common/types/Pagination';
import { useCallback, useMemo } from 'react';

import { JobRepository, SearchJobsParams } from 'domain/repositories/IJobRepository';
import { PaginatedResult } from 'domain/types/PaginatedResult';
import { JobSummary } from 'domain/entities/JobSummary';
import { useHookInjection } from 'application/hooks';

const QUERY_PREFIX = 'JOBS';

export const useCaseSearchJobs = (params: Omit<SearchJobsParams, 'offset'> | null) => {
  const qClient = useQueryClient();

  const { searchJobs } = useHookInjection<JobRepository>(JobRepository);

  const initialPageParam: Pagination = useMemo(
    () => ({
      limit: params?.limit ?? 10,
      offset: 0,
    }),
    [params?.limit],
  );

  const { data, isLoading, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage } =
    useInfiniteQuery<
      PaginatedResult<JobSummary>,
      DefaultError,
      InfiniteData<PaginatedResult<JobSummary>>,
      [string, typeof params],
      Pagination
    >({
      queryKey: [QUERY_PREFIX, params],
      enabled: !!params,
      queryFn: ({ queryKey, pageParam }) => searchJobs({ ...pageParam, ...queryKey[1] }),
      getNextPageParam: (lastPage, _pages, lastPageParam) => {
        if (lastPageParam.offset + lastPageParam.limit < lastPage.total) {
          return {
            ...lastPageParam,
            offset: lastPageParam.offset + lastPageParam.limit,
          };
        }

        return null;
      },
      initialPageParam,
      maxPages: 100,
      staleTime: 10_000,
      refetchInterval: 30_000,
      placeholderData: {
        pages: [{ items: [], total: 0 }],
        pageParams: [initialPageParam],
      },
    });

  const allPages = useMemo(
    () => data?.pages.map((page) => page.items).flat(1) ?? [],
    [data?.pages],
  );

  const refetch = useCallback(
    () => qClient.invalidateQueries({ queryKey: [QUERY_PREFIX] }),
    [qClient],
  );

  return {
    pages: data?.pages ?? [],
    allPages,
    total: data?.pages[0]?.total ?? 0,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  };
};
